<script setup lang="ts">
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import type { SectionOverviewRecord, CtaRecord } from '~/types/datocms'

const props = defineProps<{
  data: SectionOverviewRecord
  moduleIndex: number
}>()

const activeIdx = ref(0)
const ctxRef = ref<HTMLElement | null>(null)
const wrapperRef = ref<HTMLElement | null>(null)
const { isMobile } = useDevice()

const handleExternalLink = (button: CtaRecord) => {
  if (!button.recordLink && !button.externalLink) {
    console.warn(`No link provided for "${button.title}" call to action`, button)
    return undefined
  } else if (button.externalLink?.startsWith('#')) {
    return {
      path: useLocalePathByType(
        button.recordLink?._modelApiKey ?? '',
        button.recordLink?.slug as string
      ),
      hash: button.externalLink
    }
  } else if (button.externalLink?.startsWith('?')) {
    return (
      useLocalePathByType(
        button.recordLink?._modelApiKey ?? '',
        button.recordLink?.slug as string
      ) + button.externalLink
    )
  } else if (button.externalLink?.startsWith('http')) {
    return button.externalLink
  } else if (button.recordLink) {
    return useLocalePathByType(button.recordLink?._modelApiKey, button.recordLink?.slug as string)
  }
}

let ctx: gsap.Context | null = null

onMounted(() => {
  gsap.registerPlugin(ScrollTrigger)

  ctx = gsap.context(() => {
    const numItems = props.data.services.length

    ScrollTrigger.create({
      trigger: isMobile ? wrapperRef.value : ctxRef.value,
      start: 'top top',
      end: `+=${numItems * 40}%`,
      scrub: 0.2,
      pin: true,
      snap: {
        snapTo: 1 / (numItems - 1),
        duration: 0.1,
        ease: 'none'
      },
      onUpdate: (self) => {
        const idx = Math.round(self.progress * (numItems - 1))
        if (idx !== activeIdx.value) {
          activeIdx.value = idx
        }
      }
    })
  }, ctxRef.value as HTMLElement)
})

onUnmounted(() => {
  if (ctx) ctx.revert()
})
</script>

<template>
  <section ref="ctxRef" class="bg-black pb-24 pt-20 lg:py-30">
    <div class="container grid-default gap-y-20">
      <div class="col-span-full flex flex-col text-grey-light lg:col-span-4">
        <span
          v-if="data.titleProductionServices"
          class="mb-4 inline-block text-sm uppercase lg:mb-6"
          >{{ data.titleProductionServices }}</span
        >
        <ul v-if="data.productionServices?.length" class="flex flex-col gap-2 text-2xl uppercase">
          <li v-for="(item, index) in data.productionServices" :key="index">
            {{ item.title }}
          </li>
        </ul>
        <HyperLink
          v-if="data.link"
          class="mt-3 w-fit py-1 uppercase text-cream transition-colors duration-300 hover:text-yellow lg:mt-4 lg:py-2"
          :to="handleExternalLink(data.link)"
          :target="data.link.externalLink?.startsWith('http') ? '_blank' : undefined"
          :content="data.link.title ?? ''"
        />
      </div>
      <div ref="wrapperRef" class="col-span-full text-grey-light lg:col-span-6 lg:col-start-7">
        <span v-if="data.titleServices" class="mb-4 inline-block text-sm uppercase lg:mb-6">{{
          data.titleServices
        }}</span>
        <AccordionPanel
          v-for="(item, idx) in data.services"
          :id="'service-' + idx"
          :key="idx"
          :idx="idx"
          :active="activeIdx === idx"
          :icon="false"
          class="transition-colors duration-300"
          :class="activeIdx === idx && 'text-yellow'"
          @expand="activeIdx = activeIdx !== idx ? idx : null"
        >
          <template #title>
            <span class="inline-block text-left text-2xl uppercase">{{ item.title }}</span>
          </template>
          <template #content>
            <div class="pt-3 text-base" v-html="item.html" />
          </template>
        </AccordionPanel>
      </div>
    </div>
  </section>
</template>
